import * as React from "react";
import { Link } from "wouter";

import { cn } from "@/lib/cn";

/**
 * EntityCodeBadge
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.3
 *
 * Renders the entity's secondary code (e.g. "ACME-001") in muted text under
 * a primary name (e.g. "Acme Corp"). Optionally wraps the code in a `Link`
 * to the entity's detail page for quick navigation.
 *
 *   <EntityCodeBadge code="ACME-001" href="/accounts/123" />
 *
 * Use on quote/order/PO header summaries and account-hierarchy displays.
 * Does NOT render anything if `code` is null/undefined/empty.
 */
export interface EntityCodeBadgeProps {
  code?: string | null;
  /** When set, the code is wrapped in a `<Link>` (wouter). */
  href?: string;
  /** Optional prefix (e.g. "Parent: "). */
  prefix?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function EntityCodeBadge({
  code,
  href,
  prefix,
  className,
  "data-testid": dataTestId,
}: EntityCodeBadgeProps) {
  if (!code) return null;

  const inner = (
    <span
      data-testid={dataTestId ?? `text-entity-code-${code}`}
      className={cn(
        "inline-flex items-center gap-1 text-xs font-mono text-muted-foreground",
        className
      )}
    >
      {prefix}
      <span>{code}</span>
    </span>
  );

  if (href) {
    return (
      <Link
        href={href}
        className="hover:text-foreground transition-colors"
      >
        {inner}
      </Link>
    );
  }

  return inner;
}
